// eslint-disable-next-line import/no-unresolved
import { csSyncDataSubject$, SyncDataSubject } from '@creditsights/cs-web';
import {
  Button,
  ButtonMode,
  DataLoadError,
  DataView,
  Divider,
  Icon,
  ICONS,
  KeyTypes,
  Loader,
  ActionButton,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import { GENERIC_ERROR_TEXT, MY_CONTENT_ROUTES, ROUTES } from '../../../Base.constants';
import { MODAL_TYPES, useGlobalModalContext } from '../../../components/GlobalModal';
import SidePanel from '../../../components/SidePanel/SidePanel';
import { useToast } from '../../../hooks/toast/useToast';
import {
  useCreateWatchList,
  useDeleteWatchList,
  useFetchWatchLists,
  useUpdateWatchList,
} from '../../../hooks/watchLists';
import { UpdateWatchListPayload } from '../../../hooks/watchLists/useUpdateWatchList';
import { NotificationMode, RiskProductAlert } from '../../../models/Preference';
import { WatchList } from '../../../models/WatchList';
import { generateNextDefaultName } from '../../../utils/generateNextDefaultName';
import { sortWatchlists } from '../../../utils/watchListsUtils';
import NoContent from '../NoContent/NoContent';
import { constructNewEmptyWatchlist, isNewWatchlist, viewConfig } from './WatchlistsTab.logic';

import styles from './WatchlistsTab.module.scss';
import { DEFAULT_WATCHLIST_NAME, NEW_WATCHLIST_TEMP_ID } from './WatchlistTab.constants';

const cx = classNames.bind(styles);

export interface UpdateWatchlistConfig {
  onSuccess?: (watchlist: WatchList) => void;
  onError?: () => void;
}

export interface WatchlistsOutletContext {
  onNewWatchlistClick: () => void;
  onNewWatchlistCreate: (name: string) => void;
  onWatchlistUpdate: (payload: UpdateWatchListPayload, config?: UpdateWatchlistConfig) => void;
  newEmptyWatchlist?: WatchList;
  isLoading: boolean;
  watchlists?: WatchList[];
  isWatchlistNameChanging: boolean;
}

const WatchlistsTab = (): React.ReactElement | null => {
  const { id = '' } = useParams<string>();
  const navigate = useNavigate();
  // Needed for logic of navigation to see most up-to-date active watchlist on watchlist create success when user navigates to other watchlist during the creation.
  const activeWatchlistNameRef = useRef<string>('');
  const [activeWatchlistName, setActiveWatchlistName] = useState('');
  const [watchlists, setWatchlists] = useState<WatchList[]>();
  const [newEmptyWatchlist, setNewEmptyWatchlist] = useState<WatchList>();
  const isWatchlistsTabRoute = useMatch(`${ROUTES.myContent}/${MY_CONTENT_ROUTES.watchlists()}`);
  const { data, isLoading, isError, refetch } = useFetchWatchLists();
  const { mutate: createWatchlist, isLoading: isCreatingWatchlist } = useCreateWatchList();
  const { mutate: updateWatchlist, isLoading: isUpdatingWatchlist } = useUpdateWatchList();
  const [selectedItems, setSelectedItems] = useState<KeyTypes[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { mutateAsync: deleteWatchlist, isLoading: isDeleting } = useDeleteWatchList();
  const { showToast } = useToast();
  const { showModal, closeModal, setError, setProps } = useGlobalModalContext();
  const isListLoading = isLoading || !watchlists;
  const isWatchlistNameChanging = isCreatingWatchlist || isUpdatingWatchlist;

  useEffect(() => {
    if (data) {
      setWatchlists(sortWatchlists(data));
    }
  }, [data]);

  useEffect(() => {
    if (isModalOpen) {
      const newItems = selectedItems.filter((item) => watchlists?.some((watchlist) => watchlist.name === item));
      setSelectedItems(newItems);
      setProps(MODAL_TYPES.CONFIRM_DELETE_WATCHLIST, {
        onConfirm: onWatchlistDeleteConfirm,
        isMultipleWatchlists: newItems.length >= 2,
      });
    }
  }, [watchlists]);

  useLayoutEffect(() => {
    const newActiveWatchlistName = watchlists?.find((watchlist) => watchlist.id === id)?.name || '';
    if (watchlists && id && activeWatchlistName !== newActiveWatchlistName) {
      const activeWatchlistName =
        isNewWatchlist(id) && newEmptyWatchlist ? newEmptyWatchlist.name : newActiveWatchlistName;
      setActiveWatchlistName(activeWatchlistName);
      activeWatchlistNameRef.current = activeWatchlistName;
    }
  }, [id, activeWatchlistName, watchlists]);

  const syncNewWatchlistsState = (updatedWatchlists: WatchList[], newWatchlist: WatchList) => {
    setActiveWatchlistName(newWatchlist.name);
    setWatchlists(updatedWatchlists);
    setNewEmptyWatchlist(newWatchlist);
  };

  const onNewWatchlistClick = () => {
    if (!watchlists) {
      return;
    }
    const generatedName = generateNextDefaultName(
      watchlists.map((watchlist) => watchlist.name),
      DEFAULT_WATCHLIST_NAME
    );
    const emptyWatchlist = constructNewEmptyWatchlist(generatedName, watchlists.length);
    const updatedWatchlists = [...watchlists];
    updatedWatchlists.unshift(emptyWatchlist);
    syncNewWatchlistsState(updatedWatchlists, emptyWatchlist);
    navigate(NEW_WATCHLIST_TEMP_ID);
  };

  // Triggers creation of watchlitst flow if user manually enters watchlists/new url
  useEffect(() => {
    if (isNewWatchlist(id) && !newEmptyWatchlist?.name && !isListLoading) {
      onNewWatchlistClick();
    }
  }, [newEmptyWatchlist?.name, isListLoading, id]);

  if (isError) {
    return <DataLoadError onRetryClick={refetch} />;
  }

  if (watchlists && !watchlists.length && !isListLoading) {
    return (
      <NoContent
        dataTestId="no-watchlist-saved"
        header="No Watchlist yet"
        infoMessage="When you create watchlists, they will appear here."
        iconName={ICONS.NoContent}
        buttonParams={{
          iconName: ICONS.Plus,
          ariaLabel: 'Create Watchlist',
          title: 'Create Watchlist',
          onClick: onNewWatchlistClick,
        }}
      />
    );
  }

  if (isWatchlistsTabRoute && watchlists?.length) {
    return <Navigate to={MY_CONTENT_ROUTES.watchlists(watchlists[0].id, true)} replace />;
  }

  const onNewWatchlistCreate = (watchlistName: string) => {
    if (!watchlists) {
      return;
    }
    const updatedWatchlist = [...watchlists];
    const createdWatchlistIndex = updatedWatchlist.findIndex((watchlist) => watchlist.name === newEmptyWatchlist?.name);
    if (createdWatchlistIndex !== -1) {
      updatedWatchlist[createdWatchlistIndex].name = watchlistName;
      setWatchlists(updatedWatchlist);
    }
    createWatchlist(
      {
        name: watchlistName,
        myCS: true,
        tags: [],
        mode: NotificationMode.None,
        riskProductAlert: RiskProductAlert.None,
      },
      {
        onSuccess: (newWatchlist) => {
          if (createdWatchlistIndex !== -1) {
            updatedWatchlist[createdWatchlistIndex] = newWatchlist;
            syncNewWatchlistsState(updatedWatchlist, newWatchlist);
            if (activeWatchlistNameRef.current === newEmptyWatchlist?.name) {
              navigate(newWatchlist.id);
            }
          }
          csSyncDataSubject$.next(SyncDataSubject.USER_PREFERENCE);
        },
      }
    );
  };

  const onWatchlistUpdate = (payload: UpdateWatchListPayload, config?: UpdateWatchlistConfig) => {
    if (!watchlists) {
      return;
    }
    const updatedWatchlists = [...watchlists];
    const updatedWatchlistIndex = updatedWatchlists.findIndex((watchlist) => watchlist.id === payload.id);
    const previousWatchlistName = updatedWatchlists[updatedWatchlistIndex]?.name;
    const isWatchlistSelected = selectedItems.includes(previousWatchlistName);
    const updatedWatchlistName = payload.watchList.name;
    if (updatedWatchlistIndex !== -1) {
      updatedWatchlists[updatedWatchlistIndex] = {
        ...updatedWatchlists[updatedWatchlistIndex],
        ...payload.watchList,
      };

      setWatchlists(updatedWatchlists);
      if (isWatchlistSelected && updatedWatchlistName) {
        setSelectedItems((previousSelectedItems) => [...previousSelectedItems, updatedWatchlistName]);
      }
    }
    updateWatchlist(payload, {
      onSuccess: (watchlist) => {
        config?.onSuccess?.(watchlist);
        csSyncDataSubject$.next(SyncDataSubject.USER_PREFERENCE);
      },
      onError: () => {
        if (isWatchlistSelected && updatedWatchlistName) {
          setSelectedItems((previousSelectedItems) =>
            previousSelectedItems.filter((selectedWatchlist) => selectedWatchlist !== updatedWatchlistName)
          );
        }
        config?.onError?.();
      },
    });
  };

  const onWatchlistDeleteClick = () => {
    setIsModalOpen(true);
    showModal(MODAL_TYPES.CONFIRM_DELETE_WATCHLIST, {
      onConfirm: onWatchlistDeleteConfirm,
      isMultipleWatchlists: selectedItems.length >= 2,
    });
  };

  const onWatchlistDeleteConfirm = async () => {
    const selectedWatchlists = watchlists?.filter((item) => selectedItems.includes(item.name));
    const deleteRequests = selectedWatchlists!.map((watchlist) => deleteWatchlist(watchlist.id));

    try {
      await Promise.all(deleteRequests);
      onDeleteWatchlistSuccess();
    } catch {
      setError(GENERIC_ERROR_TEXT);
    }
  };

  const onDeleteWatchlistSuccess = () => {
    if (!watchlists) {
      return;
    }
    const watchlistName = watchlists.find((watchlist) => watchlist.name === selectedItems[0]);
    setSelectedItems([]);
    setIsModalOpen(false);
    closeModal();
    csSyncDataSubject$.next(SyncDataSubject.USER_PREFERENCE);

    const toastTextContent =
      selectedItems.length >= 2 ? (
        'Your watchlists have been deleted'
      ) : (
        <span>
          Your watchlist <strong>{watchlistName?.name}</strong> has been deleted
        </span>
      );
    showToast(toastTextContent);
    const updatedWatchlistList = watchlists.filter(
      (deletedWatchlist) => !selectedItems.includes(deletedWatchlist.name)
    );
    const isActiveWatchlistDeleted = !updatedWatchlistList.find((watchlist) => watchlist.id.includes(id));
    if (updatedWatchlistList.length && isActiveWatchlistDeleted) {
      navigate(MY_CONTENT_ROUTES.watchlists(updatedWatchlistList[0].id, true));
    }
  };

  return (
    <div className={cx('body')}>
      <SidePanel isLoading={isListLoading}>
        <DataView
          data={watchlists || []}
          getKey={(watchList: WatchList) => watchList.name}
          config={viewConfig}
          classNames={{ dataItemContent: cx('watchlist-item') }}
          activeKeys={[activeWatchlistName]}
          searchable
          searchByFields={['name']}
          searchEmptyText="No watchlists found. Try searching for a valid watchlist name."
          selectable
          onSelectionChange={setSelectedItems}
          selectedItems={selectedItems}
          bulkActionButtons={
            <ActionButton
              icon={ICONS.Delete}
              isLoading={isDeleting}
              onClick={onWatchlistDeleteClick}
              tooltip="Delete"
              dataTestId="bulk-delete-button"
              ariaLabel="Delete"
            />
          }
          rightSideActionButtons={
            <Button
              dataTestId="add-watchlist-btn"
              mode={ButtonMode.TERTIARY}
              onClick={onNewWatchlistClick}
              disabled={isListLoading || isCreatingWatchlist}
              loading={isCreatingWatchlist}
            >
              <Icon name={ICONS.Plus} height={12} width={12} /> New
            </Button>
          }
        />
        <Divider />
      </SidePanel>
      <div data-testid="right-side-container" className={cx('right-side-container')}>
        {isListLoading && !id && (
          <div className={cx('loader-container')}>
            <Loader position="center" />
          </div>
        )}
        <Outlet
          context={{
            watchlists,
            onNewWatchlistCreate,
            onNewWatchlistClick,
            newEmptyWatchlist,
            onWatchlistUpdate,
            isLoading: isListLoading,
            isWatchlistNameChanging,
          }}
        />
      </div>
    </div>
  );
};

export default WatchlistsTab;
